import React, { useEffect, useState } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import { SUBMIT_FEATURE_REQUEST_AND_BUG_URL } from 'src/config/ExternalLinks';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { type NamedAppliedModification, type TaskConflict } from 'src/services/jmr/types';
import { setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { createDataCyForJob, hasJobBeenModified, type TableDataRow } from 'src/utils/jmrUtils';
import { getRequirementColor, getRequirementQuantity } from 'src/utils/placementSelectionUtils';

interface ApplyModificationsModalProps {
  jobToModify: TableDataRow | null;
  isOpen: boolean;
  exceededRestrictionIds: Set<number>;
  onClose: () => void;
  focusSelection: (resultId: string) => void;
}
const ApplyModificationsModal = ({
  jobToModify,
  isOpen,
  exceededRestrictionIds,
  onClose,
  focusSelection,
}: ApplyModificationsModalProps) => {
  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>({});
  const [freeFormTextState, setFreeFormTextState] = useState<Record<string, string>>({});
  const { jmrResults, backgroundFormData } = useAppSelector((state) => state.newJmrPage);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (jobToModify?.conflicts?.length) {
      const newCheckboxState: Record<string, boolean> = {};
      const newFreeFormTextState: Record<string, string> = {};
      jobToModify.conflicts.forEach((conflict) =>
        conflict.appliedModifications?.forEach((appliedModifications) => {
          const key = hashCheckbox(conflict.taskId, appliedModifications.modificationId);
          newCheckboxState[key] = true;
          if (appliedModifications.freeFormText) newFreeFormTextState[key] = appliedModifications.freeFormText;
        }),
      );
      setCheckboxState(newCheckboxState);
      setFreeFormTextState(newFreeFormTextState);
    }
  }, [jobToModify?.conflicts]);

  const isValid = () =>
    jobToModify?.conflicts!.every((conflict) => 
      requiredInfoProvided(conflict) && (+conflict.restrictedValue > 0 || hasBeenModified(conflict))
    );

  const hasBeenModified = (conflict: TaskConflict) =>
    conflict.availableModifications.some(
      (modification) => checkboxState[hashCheckbox(conflict.taskId, modification.id)],
    );

  const requiredInfoProvided = (conflict: TaskConflict) => 
    conflict.availableModifications?.every(
      (modification) => // valid when...
        !checkboxState[hashCheckbox(conflict.taskId, modification.id)] // modification is not selected
        || !modification.isFreeFormTextAllowed // or modification does not allow other text
        || freeFormTextState[hashCheckbox(conflict.taskId, modification.id)]?.length > 0 // or free form text has been entered
    );

  const hashCheckbox = (taskId: number, modificationId: number) => `${taskId}-${modificationId}`;

  const _getRequirementColor = (conflict: TaskConflict) => getRequirementColor(conflict, exceededRestrictionIds);

  const getModifiedConflicts = (): TaskConflict[] =>
    jobToModify!.conflicts.map((conflict) => ({
      ...conflict,
      appliedModifications: conflict.availableModifications
        .filter((modification) => checkboxState[hashCheckbox(conflict.taskId, modification.id)])
        .map<NamedAppliedModification>((m) => ({
          taskId: conflict.taskId,
          modificationId: m.id,
          name: m.name,
          freeFormText: freeFormTextState[hashCheckbox(conflict.taskId, m.id)],
        })),
    }));

  const saveModifications = (modifiedConflicts: TaskConflict[], isChecked: boolean) => {
    const modifiedJmrResults = jmrResults.map((row) =>
      jobToModify?.id === row.id
        ? {
            ...row,
            conflicts: modifiedConflicts,
            isChecked,
            totalHours: !isChecked ? '0' : row.totalHours,
            // sum the required hours of unmodified conflicts
            maximumHoursAssociateCanDo: modifiedConflicts
              .filter((c) => !c.appliedModifications?.length)
              .reduce(
                (acc, c) => Math.min(Math.floor(+c.restrictedValue / +c.requirementProportion), acc),
                backgroundFormData.shiftLength,
              ),
          }
        : row,
    );
    dispatch(setJmrResults(modifiedJmrResults));
  };

  const onSaveAndApply = (modifiedConflicts: TaskConflict[]) => {
    saveModifications(modifiedConflicts, true);
    focusSelection(jobToModify!.id);
    dispatch(
      createToast({
        type: 'success',
        message: 'Job was modified and selected',
        timeout: TOAST_TIMEOUT,
      }),
    );
    onClose();
  };

  const onRemoveModifications = () => {
    saveModifications(
      jobToModify!.conflicts.map((conflict) => ({ ...conflict, appliedModifications: [] })),
      false,
    );

    dispatch(
      createToast({
        type: 'success',
        message: 'All modifications were removed and job was unselected',
        timeout: TOAST_TIMEOUT,
      }),
    );
    onClose();
  };

  return (
    <Modal
      title={`Modify: ${jobToModify?.name}`}
      open={isOpen}
      onClose={onClose}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="700px"
      scrollContainer="modal"
    >
      <div>
        <Text type="b300">
          Review the available modifications carefully to ensure they are appropriate for the specific role.{' '}
          <strong>If approved, partner with Operations to implement and purchase any items needed. </strong> Modifying a
          job will apply a 180 day limit to the job placement. Missing modification? Please submit a{' '}
          <Link type="secondary" target="_blank" href={SUBMIT_FEATURE_REQUEST_AND_BUG_URL}>
            SIM ticket
          </Link>
          .
        </Text>
      </div>
      {!!jobToModify && (
        <Column spacingInset={'400'}>
          {jobToModify.conflicts.map((conflict) => (
            <Box key={conflict.taskId} spacingInset={'400'} type="outline">
              <Row alignmentHorizontal="justify">
                <Row spacing="200">
                  <Text color={_getRequirementColor(conflict)} type="h100">
                    Requirement: {conflict.taskName}
                  </Text>
                  {conflict.requirementProportion && (
                    <Text color={_getRequirementColor(conflict)}>
                      ({getRequirementQuantity(jobToModify, conflict)} of shift)
                    </Text>
                  )}
                </Row>
                <Tag type={hasBeenModified(conflict) ? 'success' : 'warning'}>
                  {hasBeenModified(conflict) ? 'Modified' : 'Conflict'}
                </Tag>
              </Row>
              <Text>
                <strong>Conflict: </strong>
                {`Associate cannot ${conflict.taskName}${conflict.restrictedValue ? ` (${conflict.restrictedValue} Hours)` : ''}`}
              </Text>
              <Column spacing="200" spacingInset={'400 none none none'}>
                <Text type="h100">Select a Modification</Text>
                {conflict.availableModifications?.map((modification, i) => (
                  <Column key={i}>
                    <Row>
                      <Checkbox
                        checked={checkboxState[hashCheckbox(conflict.taskId, modification.id)]}
                        aria-label={modification.name}
                        onChange={(isChecked) => {
                          setCheckboxState({
                            ...checkboxState,
                            [hashCheckbox(conflict.taskId, modification.id)]: isChecked,
                          });
                          if (!isChecked) {
                            const { [hashCheckbox(conflict.taskId, modification.id)]: _, ...rest } = freeFormTextState;
                            setFreeFormTextState(rest);
                          }
                        }}
                        data-cy={`${createDataCyForJob(jobToModify.name)}-modification-${i}`}
                      />
                      <Text type="b300">{modification.name}</Text>
                    </Row>
                    {checkboxState[hashCheckbox(conflict.taskId, modification.id)] &&
                    modification.isFreeFormTextAllowed ? (
                      <Column spacing="100">
                        <Text>
                          Enter a proposed modification (required). <i>Do not share sensitive information</i>
                        </Text>
                        <Text color="secondary" type="b200">
                          {freeFormTextState[hashCheckbox(conflict.taskId, modification.id)]?.length ?? 0}/250
                        </Text>
                        <Textarea
                          value={freeFormTextState[hashCheckbox(conflict.taskId, modification.id)] ?? ''}
                          onChange={(value) =>
                            setFreeFormTextState({
                              ...freeFormTextState,
                              [hashCheckbox(conflict.taskId, modification.id)]: value.slice(0, 250),
                            })
                          }
                          placeholder="Enter modification (required)..."
                        />
                      </Column>
                    ) : null}
                  </Column>
                ))}
              </Column>
            </Box>
          ))}
        </Column>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {!!jobToModify && hasJobBeenModified(jobToModify) && (
            <Button type="tertiary" onClick={onRemoveModifications}>
              Remove Modification
            </Button>
          )}
          <Button type="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-cy={'new-jmr__results__apply-modifications'}
            type="primary"
            disabled={!isValid()}
            onClick={() => onSaveAndApply(getModifiedConflicts())}
          >
            Apply & Select
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default ApplyModificationsModal;
